import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, NgForm } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UsersSelectorComponent } from "@app/selection/users-selector/users-selector.component";
import { TotalInventoryCompletion } from "@app/_models/totalInventoryCompletion";
import { ITuple } from "@app/_models/tuple";
import { AssetGroup } from "@app/_models/_DTOs/assetGroup";
import { Department } from "@app/_models/_DTOs/department";
import { Employee } from "@app/_models/_DTOs/employee";
import { Inventory } from "@app/_models/_DTOs/inventory";
import { LegalEntity } from "@app/_models/_DTOs/legalEntity";
import { Location } from "@app/_models/_DTOs/location";
import { User } from "@app/_models/_DTOs/user";
import { InventoryViewModel } from "@app/_models/_viewModels/inventory.viewmodel";
import { InventoryService } from "@app/_services/inventory.service";
import { LegalEntityService } from "@app/_services/legalEntity.service";
import { UserService } from "@app/_services/user.service";
import { NzMessageService } from "ng-zorro-antd";

@Component({
  selector: "app-inventory-create",
  templateUrl: "./inventory-create.component.html",
  styleUrls: ["./inventory-create.component.css"],
})
export class InventoryCreateComponent implements OnInit {
  @ViewChild("createInventoryForm", { static: false })
  createInventoryForm: NgForm;

  @ViewChild(UsersSelectorComponent, { static: false })
  private usersSelectorComponent: UsersSelectorComponent;

  isEditing: boolean = false;
  allStepsComplete: boolean = false;

  progressFormat = (percent: number) => {
    return `${Math.round((percent / 100) * this.steps.length)} / ${this.steps.length
      }`;
  };
  steps: string[] = [
    "Osnovne informacije",
    "Odabir komisije",
    "Odabir popisa za inventuru",
    "Pregled",
  ];
  stepNumber: number = 0;
  status: string = "";

  inventoryViewModel: InventoryViewModel;
  isSavingInventory: boolean = false;
  errorMessages = {};

  selectedUsers: User[] = [];
  selectedLegalEntities: LegalEntity[] = [];
  selectedDepartments: Department[] = [];
  selectedLocations: Location[] = [];
  selectedAssetGroups: AssetGroup[] = [];
  selectedEmployees: Employee[] = [];
  
  includeOs: boolean = true;
  includeSi: boolean = true;

  employeePanelActive: boolean = true;
  legalEntityPanelActive: boolean = false;
  locationPanelActive: boolean = false;
  groupPanelActive: boolean = false;
  departmentPanelActive: boolean = false;

  legalEntities: LegalEntity[];

  selectedAssetCount: number = 0;
  existingInventoryCompletion: TotalInventoryCompletion;

  showAssetSelection: boolean = false;

  ignoreDiscardedAssets: boolean = true;

  locationSorts: string = "Name";
  locationFilter: string = "";
  departmentFilter: string = "";
  employeeFilter: string = "";

  inventoryResultId: number = undefined;

  constructor(
    private inventoryService: InventoryService,
    private legalEntityService: LegalEntityService,
    private router: Router,
    private messageService: NzMessageService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.legalEntityService.getAllBasic().subscribe((data: LegalEntity[]) => {
      this.legalEntities = data;
    });
    this.initModel();
    this.route.queryParams.subscribe((params) => {
      let inventoryId = params["id"];
      if (inventoryId != undefined) {
        this.isEditing = true;
        this.initModel(inventoryId);
      }
    });
  }

  goToList() {
    this.router.navigate(["inventory-list"]);
  }

  viewInventoryDetails() {
    if (this.inventoryResultId != undefined) {
      this.router.navigate(["inventory-list"], {
        queryParams: { id: this.inventoryResultId },
      });
    }
  }

  initModel(inventoryId: number = undefined) {
    this.ignoreDiscardedAssets = true;
    if (inventoryId == undefined) {
      this.inventoryViewModel = {
        id: -1,
        name: "",
        dateRange: [],
        selectedUserIds: [],
        selectAllAssets: false,
        legalEntityId: null,
        continueWithExistingAssets: false,
        ignoreDiscardedAssets: this.ignoreDiscardedAssets,
        legalEntityIds: [],
        departmentIds: [],
        locationIds: [],
        assetGroupIds: [],
        employeeIds: [],
        includeOs: true,
        includeSi: true
      };
    } else {
      this.inventoryService.get(inventoryId).subscribe((data: Inventory) => {
        this.inventoryViewModel = {
          id: data.id,
          name: data.name,
          dateRange: [data.startDate, data.endDate],
          selectedUserIds: data.assignedUsers.map((x) => x.id),
          selectAllAssets: false,
          legalEntityId: null,
          continueWithExistingAssets: false,
          ignoreDiscardedAssets: this.ignoreDiscardedAssets,
          legalEntityIds: [],
          departmentIds: [],
          locationIds: [],
          assetGroupIds: [],
          employeeIds: [],
          includeOs: true,
          includeSi: true
        };
      });
    }
  }

  ignoreDiscardedAssetsChanged() {
    if (!this.allStepsComplete) {
      this.getSelectedAssetCount();
    }
  }

  includeOsChanged(){
    if (!this.allStepsComplete) {
      this.getSelectedAssetCount();
    }
  }

  includeSiChanged(){
    if (!this.allStepsComplete) {
      this.getSelectedAssetCount();
    }
  }

  fillViewModel() {
    this.inventoryViewModel.selectedUserIds = this.selectedUsers.map(
      (x) => x.id
    );
    this.inventoryViewModel.legalEntityIds = this.selectedLegalEntities.map(
      (x) => x.id
    );
    this.inventoryViewModel.departmentIds = this.selectedDepartments.map(
      (x) => x.id
    );
    this.inventoryViewModel.locationIds = this.selectedLocations.map(
      (x) => x.id
    );
    this.inventoryViewModel.employeeIds = this.selectedEmployees.map(
      (x) => x.id
    );
    this.inventoryViewModel.assetGroupIds = this.selectedAssetGroups.map(
      (x) => x.id
    );
    this.inventoryViewModel.ignoreDiscardedAssets = this.ignoreDiscardedAssets;
    this.inventoryViewModel.includeOs = this.includeOs;
    this.inventoryViewModel.includeSi = this.includeSi;
  }

  fixDates() {
    if (this.inventoryViewModel.dateRange != undefined) {
      if (this.inventoryViewModel.dateRange[0] != undefined) {
        var startDate = new Date(this.inventoryViewModel.dateRange[0]);
        this.inventoryViewModel.dateRange[0] = startDate.toDateString();
      }
      if (this.inventoryViewModel.dateRange[1] != undefined) {
        var endDate = new Date(this.inventoryViewModel.dateRange[1]);
        this.inventoryViewModel.dateRange[1] = endDate.toDateString();
      }
    }
  }

  saveInventory(form: NgForm) {
    this.fixDates();
    if (!this.isEditing) {
      this.isSavingInventory = true;
      this.fillViewModel();
      this.inventoryService.create(this.inventoryViewModel).subscribe(
        (result) => {
          // this.messageService.create("success", "Inventura uspješno spremljena");
          if (result != undefined && (result as number) != undefined) {
            this.inventoryResultId = result as number;
          }
          this.allStepsComplete = true;
          this.isSavingInventory = false;
          this.status = "success";
          form.reset();
        },
        (error) => {
          if (error.ParamName) {
            if (form.controls[error.ParamName]) {
              form.controls[error.ParamName].setErrors({ custom: true });
              var errorMessage = error.Message.split("Parameter name")[0];
              this.errorMessages[error.ParamName] = errorMessage;
            }
          }
          this.isSavingInventory = false;
        }
      );
    } else {
      this.isSavingInventory = true;
      this.fillViewModel();
      this.inventoryService
        .edit(this.inventoryViewModel, this.inventoryViewModel.id)
        .subscribe(
          (result) => {
            // this.messageService.create("success", "Inventura uspješno spremljena");
            if (result != undefined && (result as number) != undefined) {
              this.inventoryResultId = result as number;
            }
            this.allStepsComplete = true;
            this.isSavingInventory = false;
            this.status = "success";
            this.isEditing = false;
            form.reset();
          },
          (error) => {
            if (error.ParamName) {
              if (form.controls[error.ParamName]) {
                form.controls[error.ParamName].setErrors({ custom: true });
                var errorMessage = error.Message.split("Parameter name")[0];
                this.errorMessages[error.ParamName] = errorMessage;
              }
            }
            this.isSavingInventory = false;
          }
        );
    }
  }

  next() {
    if (this.stepNumber < this.steps.length) {
      this.stepNumber++;
      this.processCurrentStep();
    }
  }

  back() {
    this.stepNumber--;
    this.processCurrentStep();
  }

  processCurrentStep() {
    this.status = "normal";
    this.validateAllFormFields(this.createInventoryForm.form);
    if (this.stepNumber == 2) {
      if (this.showAssetSelection) {
        this.inventoryViewModel.selectAllAssets = false;
        if (this.inventoryViewModel.continueWithExistingAssets) {
          this.getExistingAssetCount();
        } else {
          this.getSelectedAssetCount();
        }
      }
      // this.inventoryViewModel.selectAllAssets = false;
      // this.showAssetSelection = false;
      if (this.isEditing) {
        this.getExistingInventoryCompletion();
        this.getExistingAssetCount();
      }
    }
    if (this.stepNumber == 3) {
      if (!this.createInventoryForm.form.valid) {
        this.status = "exception";
      } else {
        this.status = "normal";
      }
    }
  }

  selectAllAssets() {
    this.inventoryViewModel.selectAllAssets = true;
    this.goToReview();
  }

  continueWithExistingAssets() {
    this.inventoryViewModel.continueWithExistingAssets = true;
    this.goToReview();
  }

  goToAssetSelection() {
    this.showAssetSelection = true;
  }

  goToReview() {
    if (this.inventoryViewModel.continueWithExistingAssets) {
      this.getExistingAssetCount();
    } else {
      this.getSelectedAssetCount();
    }
    this.stepNumber = 3;
    this.processCurrentStep();
  }

  getSelectedAssetCount() {
    if (!this.allStepsComplete) {
      this.fillViewModel();
      this.inventoryService.getAssetCount(this.inventoryViewModel).subscribe(
        (result) => {
          this.selectedAssetCount = result as number;
        },
        (error) => {
          console.log(error);
        }
      );
    }
  }

  getExistingAssetCount() {
    if (this.inventoryViewModel && this.inventoryViewModel.id != undefined) {
      this.inventoryService
        .getExistingAssetCount(this.inventoryViewModel.id)
        .subscribe(
          (result) => {
            this.selectedAssetCount = result as number;
          },
          (error) => {
            console.log(error);
          }
        );
    }
  }

  getExistingInventoryCompletion() {
    if (this.inventoryViewModel && this.inventoryViewModel.id != undefined) {
      this.inventoryService
        .getTotalCompletion(this.inventoryViewModel.id)
        .subscribe(
          (result) => {
            this.existingInventoryCompletion = result;
          },
          (error) => {
            console.log(error);
          }
        );
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    //{1}
    Object.keys(formGroup.controls).forEach((field) => {
      //{2}
      const control = formGroup.get(field); //{3}
      if (control instanceof FormControl) {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      } else if (control instanceof FormGroup) {
        //{5}
        this.validateAllFormFields(control); //{6}
      }
    });
  }

  userSelectionChanged(selectedUsers: User[]) {
    this.selectedUsers = selectedUsers;
  }

  legalEntitySelectionChanged(selectedLegalEntities: LegalEntity[]) {
    this.selectedLegalEntities = selectedLegalEntities;
    this.departmentFilter =
      "LegalEntityId==" + this.selectedLegalEntities.map(x => x.id.toString()).join("|") + ",";
    this.getSelectedAssetCount();
  }

  departmentSelectionChanged(selectedDepartments: Department[]) {
    this.selectedDepartments = selectedDepartments;
    this.getSelectedAssetCount();
    // var departmentIds = selectedDepartments.map(x => x.id);
    // this.locationFilter = "PersonInCharge.DepartmentId==" + departmentIds.join("|") + ",";
  }

  locationSelectionChanged(selectedLocations: Location[]) {
    this.selectedLocations = selectedLocations;
    this.getSelectedAssetCount();
  }
  assetGroupSelectionChanged(selectedAssetGroups: AssetGroup[]) {
    this.selectedAssetGroups = selectedAssetGroups;
    this.getSelectedAssetCount();
  }

  employeeSelectionChanged(selectedEmployees: Employee[]) {
    this.selectedEmployees = selectedEmployees;
    this.employeeFilter =
      "LegalEntityId==" + this.selectedLegalEntities.map(x => x.id.toString()).join("|") + ",";
    this.getSelectedAssetCount();
  }

  public removeUserFromSelection(user: User) {
    this.usersSelectorComponent.deselectItem(user);
  }
}
